import React, { FC, useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SideMenu,
  MenuItem,
  SubMenuItem,
  Header,
  Modal,
  ModalType,
  ProfileMenuItem,
} from '@code/innovance-react-ui-kit';

import {
  useUserInfoDispatch,
  useMenuState,
  useUserInfoState,
  useProfileMenuState,
} from 'src/front/contexts/SessionContext';

import { Logout, updateHeaderConfig } from 'src/front/api/apiInstance';
import { BREAK_POINTS } from 'src/front/constant';
import {
  DASHBOARD,
  IE_ERROR,
  MenuRoutes,
  LOGIN,
  RESULT,
  mapBreadcrumbText,
  APPLICATION,
  ProfileMenuRoutes,
} from 'src/front/routes/paths';

import CebLogo from 'src/front/components/CebLogo';
import useDeviceType from 'src/front/common/hooks/useDeviceType';
import getUserManual from 'src/front/common/utils/userManualUtils';

const DASHBOARD_INDEX = 0;
const Wrapper = styled.div`
  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE_PORTRAIT}) {
    width: 100%;
  }
`;

const Container = styled.div``;

const ContainerCenter = styled.div`
  position: relative;

  width: calc(100% - 105px);
  min-height: calc(100vh - 118px);

  margin-top: 35px;
  float: right;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN.PHONE_PORTRAIT}) {
    box-sizing: border-box;
    padding-right: 75px;
  }

  @media screen and(max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN.PHONE_PORTRAIT}) and (orientation: portrait) {
    padding-left: 37px;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE_PORTRAIT}) {
    box-sizing: border-box;
    width: 100%;
  }
`;

const HeaderWrapper = styled.div`
  width: calc(100% - 105px);
  float: right;

  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE_PORTRAIT}) {
    box-sizing: border-box;
    width: 100%;
  }
`;

const VerticalLine = styled.div`
  position: fixed;

  width: 8px;
  height: 100%;
  margin-right: 10px;

  background-color: ${({ theme }) => theme.palette.darkBlue};
`;

const mapMenuIcon: { [menuID: string]: string } = {
  CEA10000: '1',
  CEA20000: '2',
  CEA30000: '10',
};

const Layout: FC = ({ children }) => {
  const { t } = useTranslation();

  const history = useHistory();
  const { pathname } = useLocation();

  const { isPhone } = useDeviceType();
  const hasNoRestriction = !useUserInfoState().isAuthenticationRestricted;

  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number>(DASHBOARD_INDEX);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [navigateCallback, setNavigateCallback] = useState<() => void>();

  const { userProfileImageName } = useUserInfoState();
  const setUserInfo = useUserInfoDispatch();
  const menuList = useMenuState() as MenuItem[];
  const profileMenuList = useProfileMenuState();

  const toggleSideMenu = (): void => setIsSideMenuOpen((state) => !state);
  const toggleModal = (): void => setIsModalOpen((state) => !state);

  const isNeedConfirmationBeforeExit = (path: string): boolean => {
    const noNeedConfirmationMenuPaths = [IE_ERROR, DASHBOARD, APPLICATION, RESULT];
    return !noNeedConfirmationMenuPaths.includes(path);
  };

  const handleClickLogout = async (): Promise<void> => {
    try {
      await Logout.logout();

      updateHeaderConfig();
      setUserInfo({ isLoggedIn: false });

      history.push(LOGIN);
    } catch (data) {
      // TODO:(ramazanguclu) error handling
    }
  };

  const handleNavigateBreadcrumb = (path: string): void => {
    if (pathname === RESULT && path === RESULT) return;
    if (isNeedConfirmationBeforeExit(pathname) && pathname !== path) {
      setNavigateCallback(() => (): void => {
        history.push(path);
        setNavigateCallback(undefined);
        setIsModalOpen(false);
      });
      setIsModalOpen(true);
    } else {
      history.push(path);
    }
  };

  const sideMenuItems = useMemo(
    () =>
      menuList.map((item: MenuItem) => ({
        menuID: item.menuID,
        name: t(item.name),
        iconID: mapMenuIcon[item.menuID],
        // Intentionally commented out may be activated later
        /* subMenuList: item.subMenuList?.map(({ menuID, name }: SubMenuItem) => ({
          menuID,
          name: t(name),
        })), */
      })),
    [menuList, t]
  );

  const profileMenuItems = useMemo(
    () =>
      profileMenuList.map((item: ProfileMenuItem) => ({
        menuID: item.menuID,
        name: t(item.name),
        subMenuList: item.subMenuList?.map(({ menuID, name }: SubMenuItem) => ({
          menuID,
          name: t(name),
        })),
      })),
    [profileMenuList, t]
  );

  // TODO(ozgunbal): refactor below logic in more understandable way
  const paths = pathname.split('/').filter((path: string) => Boolean(path));

  const breadcrumbItems = paths.map((path: string, index: number) => ({
    text: t(mapBreadcrumbText[path]),
    path: paths.slice(0, index + 1).reduce((sum: string, item: string) => `${sum}/${item}`, ''),
    id: index,
  }));

  const handleNavigate = (index: number, item: MenuItem | SubMenuItem): void => {
    const path = MenuRoutes[item.menuID];
    if (isNeedConfirmationBeforeExit(pathname) && pathname !== path) {
      setIsModalOpen(true);
      setNavigateCallback(() => (): void => {
        history.push(MenuRoutes[item.menuID]);
        setNavigateCallback(undefined);
        setIsModalOpen(false);
      });
    } else {
      history.push(MenuRoutes[item.menuID]);
    }
  };

  const handleProfileMenuNavigate = (item: ProfileMenuItem | SubMenuItem): void => {
    if (item.menuID === 'CEA99200') {
      getUserManual();
    } else {
      history.push(ProfileMenuRoutes[item.menuID]);
    }
  };

  // TODO(ozgunbal): revisit side menu highligt considering breadcrumb
  useEffect(() => {
    if (pathname === DASHBOARD) {
      setSelectedMenuIndex(DASHBOARD_INDEX);
      return;
    }
    const index = sideMenuItems.findIndex(
      (item) => MenuRoutes[item.menuID] !== DASHBOARD && pathname.includes(MenuRoutes[item.menuID])
    );
    if (index !== -1) {
      setSelectedMenuIndex(index);
    }
  }, [pathname, sideMenuItems]);

  return (
    <Wrapper>
      {isModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_exitFromThisMenu_text')}
          onCancel={toggleModal}
          onSubmit={navigateCallback}
          submitText={t('general_components_confirm_button')}
          title={t('general_confirmationPopUp_title')}
          type={ModalType.Confirmation}
        />
      ) : null}
      {hasNoRestriction ? (
        <SideMenu
          copyrightText={t('general_components_copyright_text')}
          isOpen={isSideMenuOpen}
          items={sideMenuItems}
          onSelect={handleNavigate}
          onToggle={toggleSideMenu}
          selectedIndex={selectedMenuIndex}
        />
      ) : (
        <VerticalLine />
      )}
      <Container>
        <HeaderWrapper>
          <Header
            breadcrumbEnabled={hasNoRestriction && !isPhone}
            breadcrumbItemNavigate={handleNavigateBreadcrumb}
            breadcrumbItems={breadcrumbItems}
            hamburgerMenuClick={isPhone ? toggleSideMenu : undefined}
            logo={<CebLogo />}
            logoEnabled={hasNoRestriction}
            logoutClick={handleClickLogout}
            notification={hasNoRestriction && isPhone}
            onSelectItem={handleProfileMenuNavigate}
            profileMenuItems={profileMenuItems}
            profilePictureName={userProfileImageName}
            logoutEnabled
          />
        </HeaderWrapper>
        <ContainerCenter>{children}</ContainerCenter>
      </Container>
    </Wrapper>
  );
};

export default Layout;
